import React, { useState, useEffect } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import TrustBadges from './components/TrustBadges';
import ProgramsSection from './components/ProgramsSection';
import SyllabusModal from './components/SyllabusModal';
import FeeCalculator from './components/FeeCalculator';
import WhyChrist from './components/WhyChrist';
import Placements from './components/Placements';
import AdmissionSteps from './components/AdmissionSteps';
import Testimonials from './components/Testimonials';
import FAQSection from './components/FAQSection';
import BrochureModal from './components/BrochureModal';
import OtpPayuModal from './components/OtpPayuModal';
import PaymentResultModal from './components/PaymentResultModal';
import PaymentSuccessPage from './components/PaymentSuccessPage';
import PaymentFailurePage from './components/PaymentFailurePage';
import Footer from './components/Footer';
import FloatingCTA from './components/FloatingCTA';

function App() {
  const [isBrochureOpen, setIsBrochureOpen] = useState(false);
  const [selectedSyllabusCourse, setSelectedSyllabusCourse] = useState(null);
  const [activeOtpLead, setActiveOtpLead] = useState(null);
  const [isOtpPayuOpen, setIsOtpPayuOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [currentRoute, setCurrentRoute] = useState('home'); // 'home' | 'payment_success' | 'payment_failure'

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');

    if (paymentStatus === 'success') {
      setCurrentRoute('payment_success');
    } else if (paymentStatus === 'failure' || paymentStatus === 'cancel') {
      setCurrentRoute('payment_failure');
    }
  }, []);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const handleApplyClick = (course) => {
    if (currentRoute !== 'home') {
      handleReturnHome();
      setTimeout(() => {
        const heroElement = document.getElementById('hero');
        if (heroElement) heroElement.scrollIntoView({ behavior: 'smooth' });
      }, 200);
      return;
    }

    const heroElement = document.getElementById('hero');
    if (heroElement) {
      heroElement.scrollIntoView({ behavior: 'smooth' });
    }
    if (course) {
      showToast(`Selected ${course.name}. Fill out the form above to verify OTP & apply!`);
    }
  };

  const handleLeadSubmitted = (leadData) => {
    setActiveOtpLead(leadData);
    setIsOtpPayuOpen(true);
    showToast(`SMS OTP sent to +91 ${leadData.phone} via BulkSMSPlans Gateway!`);
  };

  const handleOtpPayuSuccess = (successData) => {
    showToast(`🎉 Payment Verified via PayU! Txn ID: ${successData.txnId}`);
  };

  const handleReturnHome = () => {
    setCurrentRoute('home');
    const cleanUrl = window.location.origin + window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Render Payment Success Page View
  if (currentRoute === 'payment_success') {
    return (
      <div className="App">
        <Navbar
          onOpenBrochure={() => setIsBrochureOpen(true)}
          onApplyClick={() => handleApplyClick()}
        />
        <PaymentSuccessPage
          onReturnHome={handleReturnHome}
        />
        <Footer onApplyClick={() => handleApplyClick()} />
        <BrochureModal
          isOpen={isBrochureOpen}
          onClose={() => setIsBrochureOpen(false)}
        />
      </div>
    );
  }

  // Render Payment Failure Page View
  if (currentRoute === 'payment_failure') {
    return (
      <div className="App">
        <Navbar
          onOpenBrochure={() => setIsBrochureOpen(true)}
          onApplyClick={() => handleApplyClick()}
        />
        <PaymentFailurePage
          onRetry={() => {
            handleReturnHome();
            setTimeout(() => {
              const heroElement = document.getElementById('hero');
              if (heroElement) heroElement.scrollIntoView({ behavior: 'smooth' });
            }, 300);
          }}
          onReturnHome={handleReturnHome}
        />
        <Footer onApplyClick={() => handleApplyClick()} />
        <BrochureModal
          isOpen={isBrochureOpen}
          onClose={() => setIsBrochureOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="App" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      
      {/* Toast Notification */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          top: '90px',
          right: '20px',
          zIndex: 9999,
          backgroundColor: '#0F2537',
          color: '#FEF08A',
          padding: '0.85rem 1.35rem',
          borderRadius: '12px',
          border: '1px solid #D4AF37',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          fontWeight: 600,
          fontSize: '0.9rem',
          animation: 'fadeIn 0.3s ease'
        }}>
          ✨ {toastMessage}
        </div>
      )}

      {/* Navigation Header */}
      <Navbar
        onOpenBrochure={() => setIsBrochureOpen(true)}
        onApplyClick={() => handleApplyClick()}
      />

      {/* Main Hero & Lead Form */}
      <Hero
        onLeadSubmitted={handleLeadSubmitted}
      />

      {/* Trust Badges & Accreditations */}
      <TrustBadges />

      {/* Programs Explorer with Syllabus Modal Trigger */}
      <ProgramsSection
        onSelectSyllabus={(course) => setSelectedSyllabusCourse(course)}
        onApplyClick={handleApplyClick}
      />

      {/* Why Choose Christ Online Feature Showcase */}
      <WhyChrist />

      {/* Interactive Fee & Monthly EMI Calculator */}
      <FeeCalculator
        onApplyClick={handleApplyClick}
      />

      {/* Corporate Placements & Hiring Partners */}
      <Placements />

      {/* Admission Steps Timeline */}
      <AdmissionSteps
        onApplyClick={handleApplyClick}
      />

      {/* Student Success Stories & Testimonials */}
      <Testimonials />

      {/* FAQ Accordion */}
      <FAQSection />

      {/* Footer */}
      <Footer
        onApplyClick={handleApplyClick}
      />

      {/* Sticky Mobile Floating Action Bar */}
      <FloatingCTA
        onOpenBrochure={() => setIsBrochureOpen(true)}
        onApplyClick={handleApplyClick}
      />

      {/* Syllabus Modal */}
      {selectedSyllabusCourse && (
        <SyllabusModal
          course={selectedSyllabusCourse}
          onClose={() => setSelectedSyllabusCourse(null)}
          onApplyClick={handleApplyClick}
        />
      )}

      {/* Prospectus Download Modal */}
      <BrochureModal
        isOpen={isBrochureOpen}
        onClose={() => setIsBrochureOpen(false)}
      />

      {/* BulkSMSPlans Mobile OTP & PayU Payment Gateway Modal */}
      <OtpPayuModal
        isOpen={isOtpPayuOpen}
        onClose={() => setIsOtpPayuOpen(false)}
        leadData={activeOtpLead}
        onSuccess={handleOtpPayuSuccess}
      />

      {/* PayU Payment Success / Failure Result Modal */}
      <PaymentResultModal onRetry={() => handleApplyClick()} />

    </div>
  );
}

export default App;

